import { useState, useRef, useEffect } from "react";
import axios from "axios";
import "./AgroChat.css";

// Generate a random session ID for this browser session
const SESSION_ID = `session_${Date.now()}`;

// The Flask RAG backend deployed on Render
// Set REACT_APP_CHAT_API_URL in your .env / Vercel env vars
const API_URL =
  process.env.REACT_APP_CHAT_API_URL || "https://agronexus-chatbot.onrender.com";

// Suggested questions shown at the start
const SUGGESTIONS = [
  "What spices do you sell?",
  "What's the minimum order for cumin?",
  "Do you ship to Canada?",
  "Can I get a sample?",
  "Are your products organic?",
];

export default function AgroChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "bot",
      text: "👋 Hello! I'm AgroBot, your Agronexus Trading assistant. I can help you with product information, pricing, shipping, and orders. What would you like to know?",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;

    setMessages((prev) => [...prev, { role: "user", text: msg }]);
    setInput("");
    setLoading(true);

    try {
      const res = await axios.post(`${API_URL}/chat`, {
        message: msg,
        session_id: SESSION_ID,
      });
      setMessages((prev) => [
        ...prev,
        { role: "bot", text: res.data.answer },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: "bot",
          text: "Sorry, I'm having trouble connecting. Please try again or email info@agronexustrading.in",
          error: true,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="agrochat-widget">
      {open && (
        <div className="agrochat-panel">
          <div className="chat-header">
            <div className="header-left">
              <div className="avatar">🌿</div>
              <div>
                <div className="bot-name">AgroBot</div>
                <div className="bot-status">
                  <span className="status-dot"></span>
                  Agronexus Trading Assistant
                </div>
              </div>
            </div>
            <button
              className="agrochat-close-btn"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
            >
              &times;
            </button>
          </div>

          <div className="messages">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`msg ${msg.role} ${msg.error ? "error" : ""}`}
              >
                {msg.role === "bot" && (
                  <span className="msg-avatar">🤖</span>
                )}
                {loading && i === messages.length - 1 && msg.role === "bot" ? (
                  <div className="msg-bubble typing">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                ) : (
                  <div className="msg-bubble">{msg.text}</div>
                )}
              </div>
            ))}

            {loading && (
              <div className="msg bot">
                <span className="msg-avatar">🤖</span>
                <div className="msg-bubble typing">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}

            {messages.length === 1 && !loading && (
              <div className="suggestions">
                <p className="suggestions-label">Try asking:</p>
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    className="suggestion-btn"
                    onClick={() => sendMessage(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          <div className="chat-input-area">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Ask about our spices, pricing, shipping..."
              disabled={loading}
              rows={1}
            />
            <button
              onClick={() => sendMessage()}
              disabled={loading || !input.trim()}
              className="send-btn"
            >
              {loading ? "..." : "Send"}
            </button>
          </div>
        </div>
      )}

      <button
        className="agrochat-toggle-btn"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={open ? "Close AgroBot chat" : "Open AgroBot chat"}
        title="Chat with AgroBot"
      >
        {open ? (
          <span style={{ fontSize: "22px", fontWeight: "bold", color: "#fff" }}>
            &times;
          </span>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="#fff"
            width="26"
            height="26"
            aria-hidden="true"
          >
            <path d="M12 2C6.477 2 2 6.163 2 11.33c0 2.913 1.373 5.53 3.54 7.28L4.5 22l4.27-2.14C9.8 20.27 10.88 20.5 12 20.5c5.523 0 10-4.163 10-9.17S17.523 2 12 2zm1 13H7v-2h6v2zm4-4H7V9h10v2z" />
          </svg>
        )}
      </button>
    </div>
  );
}
